import { Link } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";
import { BsCheckCircle, BsCreditCard, BsEnvelope, BsHourglassSplit } from "react-icons/bs";
import { CardComp, ButtonComp } from "..";
import { formatMoney } from "../../utils/currencyFormat";
import InvestorStatusBadge from "./InvestorStatusBadge";
import { investorButtonProps } from "./investorButtonStyles";
import UnifiedTimeline from "../shared/Timeline/UnifiedTimeline";

const PageGrid = styled.div`
  display: grid;
  gap: 1.25rem;

  @media (min-width: 992px) {
    grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
    gap: 1.5rem 2rem;
    align-items: start;
  }
`;

const Hero = styled.div`
  border-radius: 16px;
  padding: 1.5rem;
  background: linear-gradient(
    135deg,
    var(--color-primary-soft) 0%,
    rgba(49, 119, 140, 0.08) 100%
  );
  border: 1px solid #e8e8e8;
  margin-bottom: 1.25rem;

  @media (min-width: 768px) {
    padding: 1.75rem 2rem;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #f1f1f1;
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label {
    color: #525252;
    flex-shrink: 0;
  }

  .value {
    color: #383838;
    font-weight: 600;
    text-align: right;
  }
`;

const STATUS = {
  PENDING_PAYMENT: "pending_payment",
  ALLOCATED: "allocated",
  MATURED: "matured",
  DECLINED: "declined",
  CANCELLED: "cancelled",
  SUBMITTED: "submitted",
  UNDER_REVIEW: "under_review",
  APPROVED: "approved",
};

function normalizeStatus(status) {
  return String(status || "").toLowerCase().replace(/\s+/g, "_");
}

function getNextSteps(status, isPaid) {
  const s = normalizeStatus(status);

  if (s === STATUS.PENDING_PAYMENT) {
    return {
      title: "What happens next?",
      body: "Complete your online payment to confirm this reservation. Units stay soft-held only until payment succeeds or you cancel.",
      bullets: [
        { icon: BsCreditCard, text: "Use Pay now on the opportunity if you still need to finish payment" },
        { icon: BsHourglassSplit, text: "Unpaid holds can be cancelled from My requests" },
      ],
    };
  }

  if (s === STATUS.ALLOCATED || isPaid) {
    return {
      title: "What happens next?",
      body: "Your payment confirmed the reservation. No further payment is required for these units.",
      bullets: [
        { icon: BsCheckCircle, text: "Units are allocated against this opportunity" },
        { icon: BsEnvelope, text: "Watch email and this page for maturity and payout updates" },
      ],
    };
  }

  if (s === STATUS.MATURED) {
    return {
      title: "What happens next?",
      body: "This investment has matured. Keep this reference for your records.",
      bullets: [{ icon: BsCheckCircle, text: "Contact the cooperative if you need a payout statement" }],
    };
  }

  if (s === STATUS.CANCELLED || s === STATUS.DECLINED) {
    return {
      title: "What happens next?",
      body:
        s === STATUS.CANCELLED
          ? "This request is cancelled. You can browse other opportunities and submit a new request."
          : "This request was declined. You may submit a new request if you remain eligible.",
      bullets: [{ icon: BsEnvelope, text: "Reach support if you need clarification on this decision" }],
    };
  }

  return {
    title: "What happens next?",
    body: "Your request is with the cooperative. Track status updates on this page.",
    bullets: [{ icon: BsEnvelope, text: "Check your email for updates on this reference" }],
  };
}

export default function InvestmentRequestDetailContent({ request }) {
  if (!request) return null;

  const payload = request.payload || {};
  const opportunitySlug = payload.opportunityId;
  const status = request.status;
  const timeline = request.timeline || payload.timeline || [];
  const next = getNextSteps(status, request.isPaid || payload.isPaid);

  return (
    <PageGrid>
      <div>
        <Hero>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <InvestorStatusBadge
              status={request.type}
              label="Investment request"
              backgroundColor="var(--color-secondary)"
            />
            <InvestorStatusBadge status={status || "pending_payment"} />
          </div>
          <h1 className="h4 fw-bold mb-2" style={{ color: "#383838" }}>
            {payload.title || "Investment request"}
          </h1>
          <p className="text-muted small mb-0">
            Reference <strong>{request.reference || request.id}</strong> · Submitted{" "}
            {moment(request.created_at || request.submittedAt).format("DD MMM YYYY, HH:mm")}
          </p>
        </Hero>

        <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
          <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
            Request summary
          </h2>
          <DetailRow>
            <span className="label">Investment</span>
            <span className="value">{payload.title || "—"}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Units requested</span>
            <span className="value">{payload.units ?? "—"}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Unit price</span>
            <span className="value">
              {payload.unitPriceNgn != null ? formatMoney(payload.unitPriceNgn) : "—"}
            </span>
          </DetailRow>
          <DetailRow>
            <span className="label">Principal</span>
            <span className="value" style={{ color: "var(--color-primary)" }}>
              {payload.totalNgn != null ? formatMoney(payload.totalNgn) : "—"}
            </span>
          </DetailRow>
          {(request.isPaid || payload.amountPaid != null) && (
            <DetailRow>
              <span className="label">Amount paid</span>
              <span className="value">
                {formatMoney(payload.amountPaid ?? request.amountPaid)}
              </span>
            </DetailRow>
          )}
          {payload.expectedReturnPct != null ? (
            <DetailRow>
              <span className="label">Expected return (snapshot)</span>
              <span className="value">{payload.expectedReturnPct}%</span>
            </DetailRow>
          ) : null}
          {payload.projectedPayoutNgn != null ? (
            <DetailRow>
              <span className="label">Projected payout (snapshot)</span>
              <span className="value">{formatMoney(payload.projectedPayoutNgn)}</span>
            </DetailRow>
          ) : null}
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
          <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
            Contact details
          </h2>
          <DetailRow>
            <span className="label">Full name</span>
            <span className="value">{payload.investorName || "—"}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Phone</span>
            <span className="value">{payload.phone || "—"}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Preferred contact</span>
            <span className="value">{payload.contact_method || "—"}</span>
          </DetailRow>
          {payload.note ? (
            <div className="pt-3 mt-1 border-top">
              <p className="small text-muted mb-1">Your note</p>
              <p className="mb-0" style={{ color: "#45464e", lineHeight: 1.6 }}>
                {payload.note}
              </p>
            </div>
          ) : null}
        </CardComp>

        {opportunitySlug ? (
          <CardComp cardClassName="border-0 shadow-sm rounded-4">
            <h2 className="h6 fw-bold mb-2" style={{ color: "#383838" }}>
              Linked opportunity
            </h2>
            <p className="text-muted small mb-3">
              {payload.title || "View the opportunity you requested."}
            </p>
            <Link to={`/invest/investments/${opportunitySlug}`}>
              <ButtonComp
                btnText="View opportunity details"
                {...investorButtonProps.outline}
                ButtonInlineStyle={{
                  ...investorButtonProps.outline.ButtonInlineStyle,
                  minHeight: 42,
                }}
              />
            </Link>
          </CardComp>
        ) : null}
      </div>

      <aside>
        <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
          <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
            Status timeline
          </h2>
          <UnifiedTimeline
            items={timeline}
            showActor={false}
            emptyMessage="No timeline events yet."
          />
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm rounded-4">
          <h2 className="h6 fw-bold mb-2" style={{ color: "#383838" }}>
            {next.title}
          </h2>
          <p className="text-muted small mb-3" style={{ lineHeight: 1.6 }}>
            {next.body}
          </p>
          <div className="d-flex flex-column gap-2">
            {next.bullets.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.text} className="d-flex align-items-center gap-2 small text-muted">
                  <Icon aria-hidden />
                  <span>{b.text}</span>
                </div>
              );
            })}
          </div>
          {opportunitySlug ? (
            <Link to={`/invest/investments/${opportunitySlug}`} className="d-block mt-3">
              <ButtonComp
                btnText="View opportunity"
                {...investorButtonProps.secondary}
                ButtonInlineStyle={{
                  ...investorButtonProps.secondary.ButtonInlineStyle,
                  minHeight: 42,
                }}
              />
            </Link>
          ) : null}
        </CardComp>
      </aside>
    </PageGrid>
  );
}
